import React, { useMemo, useState } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import type { FlatExecuteStep, RunStageGraphEdge } from '@/types/run-stage.types';
import type { ExecuteEdgeMap, VertexMap } from '@/run-stage/hooks/useExecuteTranslations';
import { StepMetadataHeader } from './StepMetadataHeader';
import { SubstepCard } from './SubstepCard';
import { CheckpointList } from './CheckpointList';
import { DefinedVsActualDiff } from './DefinedVsActualDiff';
import { useExecuteStepStore } from '@/run-stage/store/executeStepStore';

interface StepDetailCardProps {
  flatStep: FlatExecuteStep;
  allFlatSteps: FlatExecuteStep[];
  currentIndex: number;
  isReadOnly?: boolean;
  isOffline?: boolean;
  onTimingChange?: (stepId: string, field: 'startTime' | 'endTime', value: string) => void;
  /** Indexed execute graph edges for "Actual Step" params */
  executeEdgeMap?: ExecuteEdgeMap;
  /** Indexed plan graph edges for "Defined Step" params */
  planEdgeMap?: ExecuteEdgeMap;
  /** Indexed execute graph vertices for operationsInNodes runs */
  executeVertexMap?: VertexMap;
  /** Indexed plan graph vertices for operationsInNodes runs */
  planVertexMap?: VertexMap;
  /** Run ID for saving parameter changes */
  runId?: string;
  /** Called after a parameter edit is saved */
  onParamSaved?: () => void;
}

/** Renders parameter diffs for a step if any exist */
const ParameterDiffSection: React.FC<{ stepId: string }> = ({ stepId }) => {
  const parameterDiffs = useExecuteStepStore((s) => s.parameterDiffs);
  if (!parameterDiffs || parameterDiffs.length === 0) return null;
  return <DefinedVsActualDiff diffs={parameterDiffs as never[]} stepId={stepId} />;
};

export const StepDetailCard: React.FC<StepDetailCardProps> = ({
  flatStep,
  allFlatSteps,
  currentIndex,
  isReadOnly = false,
  isOffline = false,
  onTimingChange,
  executeEdgeMap,
  planEdgeMap,
  executeVertexMap,
  planVertexMap,
  runId,
  onParamSaved,
}) => {
  const [showMore, setShowMore] = useState(false);

  const substeps = useMemo(() => {
    if (flatStep.isSubstep) return [];
    return allFlatSteps.filter(
      (f) => f.isSubstep && f.step.parentStepId === flatStep.step.stepId
    );
  }, [flatStep, allFlatSteps]);

  const hasSubsteps = substeps.length > 0;
  const visibleSubsteps = showMore ? substeps : substeps.slice(0, 3);

  const edgeKey = (step: FlatExecuteStep['step']) =>
    `${step.stepId}|${step.cloneNumber ?? 0}`;

  // Vertex-based runs use operation_* IDs, not edge_* IDs.
  // Cycle-clone steps use a composite stepId like "vertexId__c2" — strip the
  // suffix to find the canonical OperationVertex in the graph map.
  const vertexKey = (step: FlatExecuteStep['step']) => {
    const baseId = step.stepId.replace(/__c\d+$/, '');
    return `${baseId}|${step.cloneNumber ?? 1}`;
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Metadata header */}
      <StepMetadataHeader flatStep={flatStep} />

      {/* Parameter diffs from design → execute */}
      <ParameterDiffSection stepId={flatStep.step.stepId} />

      {/* Main step card with StepTranslationRow for actual + defined */}
      <SubstepCard
        step={flatStep.step}
        planStep={flatStep.planStep}
        label={flatStep.pillLabel}
        isReadOnly={isReadOnly}
        isOffline={isOffline}
        onTimingChange={onTimingChange}
        actualEdge={executeEdgeMap?.get(edgeKey(flatStep.step))}
        definedEdge={planEdgeMap?.get(edgeKey(flatStep.step))}
        actualVertex={executeVertexMap?.get(vertexKey(flatStep.step))}
        definedVertex={planVertexMap?.get(vertexKey(flatStep.step))}
        runId={runId}
        onParamSaved={onParamSaved}
      />

      {/* Substeps */}
      {hasSubsteps && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
            Substeps ({substeps.length})
          </Typography>
          {visibleSubsteps.map((sub) => (
            <SubstepCard
              key={`${sub.step.stepId}-${sub.step.cloneNumber ?? 0}`}
              step={sub.step}
              planStep={sub.planStep}
              label={sub.pillLabel}
              isReadOnly={isReadOnly}
              isOffline={isOffline}
              onTimingChange={onTimingChange}
              actualEdge={executeEdgeMap?.get(edgeKey(sub.step))}
              definedEdge={planEdgeMap?.get(edgeKey(sub.step))}
              actualVertex={executeVertexMap?.get(vertexKey(sub.step))}
              definedVertex={planVertexMap?.get(vertexKey(sub.step))}
              runId={runId}
              onParamSaved={onParamSaved}
            />
          ))}
          {substeps.length > 3 && (
            <Button
              size="small"
              onClick={() => setShowMore(!showMore)}
              startIcon={showMore ? <ExpandLess /> : <ExpandMore />}
              sx={{ textTransform: 'none' }}
            >
              {showMore ? 'Show Less' : `Show ${substeps.length - 3} More`}
            </Button>
          )}
        </Box>
      )}

      {/* Checkpoints */}
      {flatStep.checkpoints && flatStep.checkpoints.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Checkpoints
          </Typography>
          <CheckpointList
            checkpoints={flatStep.checkpoints}
            isReadOnly={isReadOnly || flatStep.step.completed === true}
          />
        </Box>
      )}

      {/* Machine checklists */}
      {flatStep.machineChecklists && flatStep.machineChecklists.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Machine Checklists
          </Typography>
          {flatStep.machineChecklists.map((cl, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {cl.checklist?.checkpointName || `Checklist ${cl.order ?? i + 1}`}
                {cl.isMandatory && (
                  <Typography component="span" variant="caption" color="error" sx={{ ml: 0.5 }}>
                    (Mandatory)
                  </Typography>
                )}
              </Typography>
              {cl.completedOn && (
                <Typography variant="caption" color="success.main">
                  Completed: {new Date(cl.completedOn).toLocaleString()}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Approval section */}
      {flatStep.step.approval?.approvers && flatStep.step.approval.approvers.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Approvals
          </Typography>
          {flatStep.step.approval.approvers.map((a, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2">{a.user?.name || a.user?.email || '—'}</Typography>
              <Typography variant="caption" color={a.status === 'APPROVED' ? 'success.main' : 'text.secondary'}>
                {a.status || 'PENDING'}
              </Typography>
              {a.approvedAt && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(a.approvedAt).toLocaleString()}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Files */}
      {flatStep.step.files && flatStep.step.files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Attachments ({flatStep.step.files.length})
          </Typography>
          {flatStep.step.files.map((f, i) => (
            <Typography key={f._id || i} variant="body2" color="primary" sx={{ cursor: 'pointer', mb: 0.25 }}>
              {f.name || `File ${i + 1}`}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};
